import lineClamp from "line-clamp"

const MORE_TEXT = "show more"
const LESS_TEXT = "show less"
const ELLIPSIS = `... ${MORE_TEXT}`

// Inline "show more" needs a single run of text; a heading or list in the
// visible lines would read wrong when cut, so those keep the CSS clamp.
const BLOCK_MARKUP = "h1, h2, h3, h4, h5, h6, ul, ol, pre, table, blockquote, img"

// Clamps a hero description to two lines on desktop and three on phone with an
// inline "... show more" that restores the full text, and a "show less" that
// clamps it again. JS truncation (not CSS line-clamp) so the toggle can sit
// inline after the ellipsis. line-clamp walks the DOM node by node, so links
// and emphasis in markdown survive the cut.
export default function ClampedDescription(element: HTMLElement | null) {
  if (!element) return

  const desktop = window.matchMedia("(min-width: 724px)")
  const originalHTML = element.innerHTML
  let expanded = false

  const keepCssClamp = () => {
    element.innerHTML = originalHTML
    element.classList.remove("js-clamped")
  }

  const clamp = () => {
    if (expanded) return

    element.innerHTML = originalHTML
    // Lifts the CSS fallback clamp so the truncation measures real layout.
    element.classList.remove("js-expanded")
    element.classList.add("js-clamped")

    const lines = desktop.matches ? 2 : 3
    if (!lineClamp(element, lines, { ellipsis: ELLIPSIS })) {
      // The text fits: show it with its normal paragraph layout.
      element.innerHTML = originalHTML
      element.classList.replace("js-clamped", "js-expanded")
      return
    }

    // line-clamp removed what is below the cut, so this checks the visible lines only.
    if (element.querySelector(BLOCK_MARKUP)) {
      keepCssClamp()
      return
    }

    const ellipsisNode = lastTextNodeWith(element, ELLIPSIS)
    if (!ellipsisNode) return

    ellipsisNode.textContent = ellipsisNode.textContent!.slice(0, -MORE_TEXT.length)

    const showMore = toggleButton(MORE_TEXT, false, expand)

    // Inside the text's own parent, so a markdown <p> keeps the toggle inline,
    // but after a markdown link the cut fell in: a link cannot hold a button.
    const cutLink = ellipsisNode.parentElement!.closest("a")
    const before = cutLink && element.contains(cutLink) ? cutLink : ellipsisNode
    before.parentNode!.insertBefore(showMore, before.nextSibling)

    // The nowrap toggle cannot split the way the measured placeholder text could,
    // so trim until it fits back on the last line. Half a line of tolerance:
    // the toggle's inline box runs a hair taller than the text lines.
    const lineHeight = parseInt(window.getComputedStyle(element).lineHeight, 10)
    const maxHeight = lines * lineHeight + lineHeight / 2
    let guard = 40
    while (element.scrollHeight > maxHeight && guard-- > 0) {
      const text = ellipsisNode.textContent!
      if (text.length <= 5) break
      ellipsisNode.textContent = `${text.slice(0, -5).trimEnd()}... `
    }

    // The cut fell in a text too short to trim: keep the CSS clamp instead.
    if (element.scrollHeight > maxHeight) keepCssClamp()
  }

  const expand = () => {
    expanded = true
    element.innerHTML = originalHTML
    element.classList.replace("js-clamped", "js-expanded")

    // At the end of the last paragraph, so it reads inline; after a list or a
    // heading it takes its own line.
    const last = element.lastElementChild
    const host = last?.matches("p") ? last : element
    host.append(" ", toggleButton(LESS_TEXT, true, collapse))

    // The toggle is gone now; keep keyboard focus at the start of the text.
    element.tabIndex = -1
    element.focus({ preventScroll: true })
  }

  const collapse = () => {
    expanded = false
    clamp()

    // The text got shorter, so its top can be above the screen now.
    if (element.getBoundingClientRect().top < 0) element.scrollIntoView({ block: "start" })

    // Without a toggle the CSS clamp took over; the text itself keeps focus.
    const showMore = element.querySelector<HTMLElement>(".clamp-toggle")
    ;(showMore ?? element).focus({ preventScroll: true })
  }

  clamp()

  // Line breaks move when the web font arrives or the width changes.
  document.fonts?.ready.then(clamp)

  let lastWidth = window.innerWidth
  let frame = 0
  window.addEventListener("resize", () => {
    if (window.innerWidth === lastWidth) return
    lastWidth = window.innerWidth
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(clamp)
  })
}

function toggleButton(label: string, isExpanded: boolean, onClick: () => void) {
  const button = document.createElement("button")
  button.type = "button"
  button.className = "clamp-toggle"
  button.textContent = label
  button.setAttribute("aria-expanded", String(isExpanded))
  button.addEventListener("click", onClick)
  return button
}

function lastTextNodeWith(root: HTMLElement, text: string) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let found: Text | null = null
  while (walker.nextNode()) {
    if (walker.currentNode.textContent?.includes(text)) found = walker.currentNode as Text
  }
  return found
}
